import { useRouteMatch } from 'quartz-routing/react-router';
import { useAppContextQuery } from '../../hooks/useAppContextQuery';
import { Paths } from '../../constants/paths';
import { GET_PLACEMENTS_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/PlacementsRpc.dfc.new';
import { GET_PLACEMENTS_QUERY as PUBLIC_GET_PLACEMENTS_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/PlacementsRpc-external.dfc.new';
import { CalloutPlacementDiscriminant } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/PlacementsRpc';
import { useMockPlacementsResponse } from '../mocks/placementsMock';

/**
 * Legacy hero CTA behaviors carried through the `useHeroData`-backed mock
 * (MKTD-808) that aren't part of the studio callout model yet. Read these off a
 * placement's `calloutContent` via {@link getCalloutCta}.
 */

/** Reads the legacy CTA fields off a callout placement's content. */
export function getCalloutCta(placement) {
  return placement.calloutContent;
}
const PATH_TO_PAGE_ID = {
  [Paths.HOME]: 'HOME',
  [Paths.APPS]: 'APPS',
  [Paths.AGENTS]: 'AGENTS',
  [Paths.SOLUTIONS_PARTNERS]: 'SOLUTIONS_PARTNERS',
  [Paths.FEATURED_COLLECTION]: 'FEATURED_COLLECTION',
  [Paths.WORKFLOW_TEMPLATES]: 'WORKFLOWS'
};

/** Maps the active route to a placements `PageId` (or undefined if unmapped). */
export function useCurrentPageId() {
  const currentMatch = useRouteMatch();
  return currentMatch !== null && currentMatch !== void 0 && currentMatch.path ? PATH_TO_PAGE_ID[currentMatch.path] : undefined;
}

/**
 * Ordered banner callout placements (`BANNER_1`, `BANNER_2`, ...) for the
 * current page. Used to render banners interleaved between page sections
 * rather than stacked under the top callout row.
 */
export const BANNER_SECTIONS = ['BANNER_1', 'BANNER_2'];
export function usePageCalloutBanners() {
  const pageId = useCurrentPageId();
  const {
    placementsBySection
  } = usePagePlacements(pageId);
  return BANNER_SECTIONS.map(section => placementsBySection[section]).filter(placement => Boolean(placement));
}
function isCalloutPlacement(p) {
  return p.entityType === CalloutPlacementDiscriminant;
}
function toPlacementsBySection(placements) {
  const placementsBySection = {};
  for (const placement of placements) {
    if (isCalloutPlacement(placement)) {
      placementsBySection[placement.section] = placement;
    }
  }
  return placementsBySection;
}

/**
 * Real, backend-backed implementation. Not currently wired up: the studio
 * `getPlacements` RPC is not yet returning the data we need for the Workflows
 * Phase 2 release (MKTD-808). Re-enable by having `usePagePlacements` delegate
 * to this instead of `useMockPagePlacements` once the backend is ready.
 */
export function useRpcPagePlacements(pageId) {
  var _data$result$placemen, _data$result;
  const {
    data,
    loading,
    error
  } = useAppContextQuery({
    app: GET_PLACEMENTS_QUERY,
    public: PUBLIC_GET_PLACEMENTS_QUERY,
    options: {
      skip: !pageId,
      variables: {
        page: pageId,
        published: true
      }
    }
  });
  const placements = (_data$result$placemen = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 ? void 0 : _data$result.placements) !== null && _data$result$placemen !== void 0 ? _data$result$placemen : [];
  return {
    placementsBySection: toPlacementsBySection(placements),
    loading,
    error
  };
}

/**
 * Temporary mock implementation backed by `useHeroData` (MKTD-808). See
 * `placementsMock.ts` for the hero-carousel -> placements layout mapping.
 */
function useMockPagePlacements(pageId) {
  const {
    placements
  } = useMockPlacementsResponse(pageId);
  return {
    placementsBySection: toPlacementsBySection(placements),
    loading: false,
    error: undefined
  };
}
export function usePagePlacements(pageId) {
  return useMockPagePlacements(pageId);
}